import { createSelector } from '@ngrx/store';
import { Collection } from 'immutable';
import { operationTypeConfig } from 'src/app/ops/config/operation-type-config';
import { RequestStatus, WaypointType } from 'src/graphql/generated/graphql.generated';
import { selectRequestLocationById } from 'src/store/generated/request-location-record.generated';
import { minutesToTimeElapsed } from 'src/util/helpers/date-helper';
import { WaypointRecord } from '../generated/waypoint-record.generated';
import { createWaypointFromRequestLocation, hasLatLng } from '../model-helpers';
import { selectCardByCardNumber } from './card-selectors';
import {
  selectAllRequestLocations,
  selectAllRequestPatients,
  selectAllRequests,
} from './entity-store-selectors';

export function selectRequestById(id: string) {
  return createSelector(selectAllRequests, (requests) => {
    return requests.find((r) => r.id === id);
  });
}

export function selectRequestByCardNumber(cardNumber: string) {
  return createSelector(selectCardByCardNumber(cardNumber), selectAllRequests, (card, requests) => {
    return card != null ? requests.find((r) => r.id === card.requestId) : undefined;
  });
}

export function selectAllPatientsByRequestId(requestId: string) {
  return createSelector(selectAllRequestPatients, (reqPatient) =>
    reqPatient.filter((rp) => rp.requestId === requestId).sortBy((a1) => a1.dateCreated),
  );
}

export function selectRequestPatientByRequestId(requestId: string) {
  return createSelector(selectAllPatientsByRequestId(requestId), (reqPatients) =>
    reqPatients.first(undefined),
  );
}

export function selectRequestPatientById(patientId: string) {
  return createSelector(selectAllRequestPatients, (reqPatient) =>
    reqPatient.find((rp) => rp.id === patientId),
  );
}

export function selectRequestConfigurationByRequestId(requestId: string) {
  return createSelector(selectRequestById(requestId), (request) => {
    if (request != null) {
      const opType = request.requestedOperationType;
      return operationTypeConfig[opType].request;
    }
    return undefined;
  });
}

export function selectInitialDestinationByRequestId(requestId: string) {
  return createSelector(
    selectRequestConfigurationByRequestId(requestId),
    selectRequestLocationsByRequestId(requestId),
    (requestConfig, locations) => {
      if (requestConfig != null) {
        return locations.find((l) => l.type === requestConfig.initialDestination);
      }
      return undefined;
    },
  );
}

export function selectTargetDestinationByRequestId(requestId: string) {
  return createSelector(
    selectRequestConfigurationByRequestId(requestId),
    selectRequestLocationsByRequestId(requestId),
    (requestConfig, locations) => {
      if (requestConfig != null) {
        return locations.find((l) => l.type === requestConfig.targetDestination);
      }
      return undefined;
    },
  );
}

export function selectRequestWaypointsByCardNumber(cardNumber: string) {
  return createSelector(
    selectRequestByCardNumber(cardNumber),
    selectAllRequestLocations,
    (request, locations) => {
      let waypoints: any[] = [];
      if (request != null) {
        const requestLocations = locations.filter((r) => r.requestId === request.id);
        switch (request.requestedOperationType) {
          default:
            waypoints = requestLocations
              .toIndexedSeq()
              .map((r) => createWaypointFromRequestLocation(r))
              .filter((r) => hasLatLng(r))
              .toArray();
        }
      }
      return Collection.Indexed<WaypointRecord>(waypoints);
    },
  );
}

export function selectRequestLocationsByRequestId(requestId: string) {
  return createSelector(selectAllRequestLocations, (loc) =>
    loc.filter((l) => l.requestId === requestId).valueSeq(),
  );
}

export function selectRequestLocationByRequestAndLocationId(requestId: string, locationId: string) {
  return createSelector(selectAllRequestLocations, (loc) => {
    return loc.find((l) => l.requestId === requestId && l.locationId === locationId);
  });
}

export function selectRequestByRequestLocationId(requestLocationId: string) {
  return createSelector(
    selectAllRequests,
    selectRequestLocationById(requestLocationId),
    (requests, loc) => {
      return loc == null ? undefined : requests.find((r) => r.id === loc.requestId);
    },
  );
}

export function selectRequestLocationByType(requestId: string, type: WaypointType) {
  return createSelector(selectRequestLocationsByRequestId(requestId), (locations) => {
    const matches = locations.filter((l) => l.type === type);
    if (matches.count() === 0) {
      return undefined;
    }
    if (matches.count() === 1) {
      return matches.get(0);
    }
    throw new Error(`Request had more than 1 ${type} location. (Request ID: ${requestId})`);
  });
}

export function selectPendingRequestsForRegion(regionId: string) {
  return createSelector(selectAllRequests, (requests) => {
    return requests.valueSeq().filter((r) => {
      return (
        (r.regionId === regionId || r.regionId == null) &&
        r.status !== RequestStatus.Assigned &&
        r.status !== RequestStatus.Cancelled
      );
    });
  });
}

export function selectRequestsByRegion(regionId: string) {
  return createSelector(selectAllRequests, (requests) => {
    return requests.filter((r) => r.regionId === regionId);
  });
}

export function selectRequestAtloDataByRequestId(requestId: string) {
  return createSelector(selectRequestById(requestId), (request) => {
    if (request != null && request.etloInMinutes != null && request.atloInMinutes != null) {
      return {
        atlo: minutesToTimeElapsed(request.atloInMinutes, 2),
        atloInMinutes: request.atloInMinutes,
        etlo: minutesToTimeElapsed(request.etloInMinutes, 2),
        etloInMinutes: request.etloInMinutes,
      };
    }
  });
}
